import clsx from 'clsx'
import { ChangeEvent, forwardRef, MutableRefObject, ReactNode, Ref, useId, useState } from 'react'

export type RadioSize = 'sm' | 'md'

export interface RadioProps {
    checked?: boolean
    defaultChecked?: boolean
    onChange?: (checked: boolean) => void
    size?: RadioSize
    label?: ReactNode
    supportingText?: ReactNode
    disabled?: boolean
    id?: string
    name?: string
    value?: string
    className?: string
}

interface SizeClasses {
    circle: string
    dot: string
    row: string
    text: string
    textGroup: string
}

const SIZE_CLASSES: Record<RadioSize, SizeClasses> = {
    sm: {
        circle: 'size-4',
        dot: 'size-1.5',
        row: 'gap-spacing-md',
        text: 'text-sm',
        textGroup: 'gap-spacing-none',
    },
    md: {
        circle: 'size-5',
        dot: 'size-2',
        row: 'gap-spacing-lg',
        text: 'text-md',
        textGroup: 'gap-spacing-xxs',
    },
}

const assignRef = (ref: Ref<HTMLInputElement> | undefined, node: HTMLInputElement | null) => {
    if (typeof ref === 'function') {
        ref(node)
    } else if (ref) {
        ;(ref as MutableRefObject<HTMLInputElement | null>).current = node
    }
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
    (
        {
            checked,
            defaultChecked,
            onChange,
            size = 'sm',
            label,
            supportingText,
            disabled = false,
            id: providedId,
            name,
            value,
            className,
        },
        ref,
    ) => {
        const generatedId = useId()
        const id = providedId || generatedId
        const sizeClasses = SIZE_CLASSES[size]

        const [internalChecked, setInternalChecked] = useState(defaultChecked ?? false)
        const isControlled = checked !== undefined
        const isChecked = isControlled ? checked : internalChecked

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            if (disabled) {
                return
            }
            const nextChecked = event.target.checked
            if (!isControlled) {
                setInternalChecked(nextChecked)
            }
            onChange?.(nextChecked)
        }

        const getCircleStateClasses = () => {
            if (isChecked) {
                return 'border-border-brand bg-background-bg-brand-solid'
            }
            if (disabled) {
                return 'border-border-primary bg-background-bg-tertiary'
            }
            return 'border-border-primary'
        }

        const circleClasses = clsx(
            'inline-flex shrink-0 items-center justify-center rounded-radius-full border border-solid',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-focus-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-base-white',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            sizeClasses.circle,
            getCircleStateClasses(),
        )

        const control = (
            <>
                <input
                    ref={(node) => assignRef(ref, node)}
                    type="radio"
                    id={id}
                    name={name}
                    value={value}
                    checked={isChecked}
                    disabled={disabled}
                    onChange={handleChange}
                    className="peer sr-only"
                />
                <label htmlFor={id} className={circleClasses}>
                    {isChecked && (
                        <span
                            aria-hidden="true"
                            className={clsx('rounded-radius-full bg-base-white', sizeClasses.dot)}
                        />
                    )}
                </label>
            </>
        )

        if (!label && !supportingText) {
            return <div className={clsx('inline-flex', className)}>{control}</div>
        }

        return (
            <div className={clsx('inline-flex items-start font-body', sizeClasses.row, className)}>
                <div className="flex shrink-0 items-center pt-spacing-xxs">{control}</div>
                <div className={clsx('flex flex-col', sizeClasses.textGroup)}>
                    {label && (
                        <label
                            htmlFor={id}
                            className={clsx(
                                'font-medium text-text-secondary',
                                sizeClasses.text,
                                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                            )}>
                            {label}
                        </label>
                    )}
                    {supportingText && (
                        <div className={clsx('font-regular text-text-tertiary', sizeClasses.text)}>
                            {supportingText}
                        </div>
                    )}
                </div>
            </div>
        )
    },
)

Radio.displayName = 'Radio'
